import { formatExternalUrl } from '@/utils/formatExternalUrl';
import {
  Box,
  Center,
  Divider,
  HStack,
  IconButton,
  Stack,
  Text,
  type BoxProps,
} from '@chakra-ui/react';
import { formatNumber } from '@onbeam/utils';
import { DiscordIcon, GlobeIcon, TwitterIcon } from '@sphere/icons';
import { rem } from 'polished';

type Props = BoxProps & {
  testId?: string;
  items: {
    label: string;
    value: number | string;
  }[];
  discord?: string;
  twitter?: string;
  externalUrl?: string;
};

export const CollectionStats = ({
  items,
  testId = 'collection-stats',
  discord,
  twitter,
  externalUrl,
  ...boxProps
}: Props) => {
  return (
    <Box
      data-testid={testId}
      borderRadius="radius.12"
      background="gray.2"
      borderWidth="1px"
      borderColor="border"
      backdropFilter="blur(10px)"
      py={{ base: 'space.16', sm: 'space.20' }}
      px={{ base: 'space.16', sm: 'space.32' }}
      width="100%"
      {...boxProps}
    >
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        spacing={{ base: 'space.16', sm: 'space.32' }}
      >
        <HStack spacing="space.16" justifyContent={{ base: 'center', sm: 'start' }}>
          {items.map(item => (
            <Box key={item.label + item.value}>
              <Text fontWeight="bold" fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}>
                {typeof item.value === 'string' ? item.value : formatNumber(item.value)}
              </Text>
              <Text fontSize={{ base: '2xs', sm: 'xs', md: 'sm' }} color="gray.1">
                {item.label}
              </Text>
            </Box>
          ))}
        </HStack>

        {(discord || twitter || externalUrl) && (
          <>
            <Center>
              <Divider
                display={{ base: 'none', sm: 'inherit' }}
                orientation="vertical"
                height={rem(32)}
                margin={0}
              />
              <Divider display={{ sm: 'none' }} orientation="horizontal" width="100%" margin={0} />
            </Center>
            <Center>
              <Stack
                flexWrap="wrap"
                // Size of 2 items + spacing
                maxH={{ base: 'unset', sm: rem(52) }}
                maxW={{ base: 'unset', sm: rem(52) }}
                direction={{
                  base: 'row',
                  sm:
                    // Row if 3 or more links are present
                    [discord, twitter, externalUrl].filter(link => !!link).length >= 3
                      ? 'row'
                      : 'column',
                }}
                justify="center"
                spacing={{ base: 'space.20', sm: rem(12) }}
              >
                {discord && (
                  <IconButton
                    as="a"
                    variant="icon"
                    icon={<DiscordIcon color="white" boxSize={rem(20)} />}
                    aria-label="Discord"
                    href={discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    boxSize={rem(20)}
                    transition="opacity 120ms ease-in-out"
                    _hover={{ opacity: 0.6 }}
                  />
                )}
                {twitter && (
                  <IconButton
                    as="a"
                    variant="icon"
                    icon={<TwitterIcon boxSize={rem(20)} />}
                    aria-label="Discord"
                    href={twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    boxSize={rem(20)}
                    transition="opacity 120ms ease-in-out"
                    _hover={{ opacity: 0.6 }}
                  />
                )}
                {externalUrl && (
                  <IconButton
                    as="a"
                    variant="icon"
                    icon={<GlobeIcon boxSize={rem(20)} />}
                    aria-label="Website"
                    href={formatExternalUrl(externalUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    boxSize={rem(20)}
                    transition="opacity 120ms ease-in-out"
                    _hover={{ opacity: 0.6 }}
                  />
                )}
              </Stack>
            </Center>
          </>
        )}
      </Stack>
    </Box>
  );
};
